import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { B, space, font, radius, weight } from '../constants/brand';
import { api } from '../api/client';
import { fmt } from '../utils/format';
import {
  Btn, Card, Modal, ModalFooter, Input, Select, PageHeader,
  Spinner, EmptyState, Chip, Icon, useToast,
} from '../components/ui';
import { usePersonalConnectStatus, NotConnectedCard } from '../components/shared/PersonalConnectGate';
import { NoteBodyEditor } from '../components/shared/NoteBodyEditor';
import { MailTriageModal } from '../components/shared/MailTriageModal';
import { FileToDealModal } from '../components/shared/FileToDealModal';
import { groupByConversation, runInChunks } from '../utils/mailThreads';
import { applyMailDelta, syncSinceFor, canDeltaSync, formatTiming, mailViewCache } from '../utils/mailSync';
import { FLAG_CHOICES, flagForChoice, flagDueLabel, MAIL_SHORTCUTS, mailShortcutFor } from '../utils/mailFlags';
import { mailVerdictChip } from '@harvus/shared';

// ── Mail — the in-app Outlook client ─────────────────────────────────────────
// Reads, sends and files the SIGNED-IN PERSON'S OWN Outlook mail — or a
// SHARED MAILBOX they have added — through the same delegated Microsoft
// connection Harvi's mail tools use (My Settings → Connected Accounts →
// "Mail & Calendar"). This page is a direct, human-driven surface: every send/
// delete/move happens because the person clicked a button in front of the
// message it acts on — there is no propose-and-review step the way there is
// for Harvi's own drafts, because clicking Send here IS the review (Undo
// Send below is the one exception, and it is a client-side delay, not a
// server recall).
//
// A message's HTML body is somebody else's content and is rendered inside a
// SANDBOXED iframe with no allow-scripts (see SafeHtmlFrame below) — the same
// reason real mail clients never dangerouslySetInnerHTML a message body.

// Keep in step with MAX_ATTACHMENT_BYTES in api/src/mailClient.js. A small
// duplicated constant, not a business rule — there is no shared module
// between the API and the browser this could live in instead.
const MAX_ATTACHMENT_BYTES = 3 * 1024 * 1024;

// An inline image is embedded as a data: URI directly in the HTML body (no
// server-side upload exists for mail), so it rides inside the same
// MAX_BODY_CHARS ceiling the API already enforces on the whole message. Kept
// well under that so one screenshot can't eat the entire budget and leave no
// room for anything else in the message — a friendly "too big" toast here
// beats a generic "message body is too long" from the API after the person
// has already written the rest of the reply.
const MAX_INLINE_IMAGE_BYTES = 300 * 1024;

// UNDO SEND — a client-side delay before the network call ever happens, the
// same mechanism Gmail and "new Outlook" actually use for it. Nothing is
// transmitted during this window; Undo just cancels the pending timeout, so
// there is nothing to "recall". See scheduledSend.js's header for why this is
// deliberately NOT the same mechanism as Schedule Send.
const UNDO_SEND_SECONDS = 8;

// Tick box for bulk selection — the same shape pages/Documents.jsx uses.
const Tick = ({ checked, indeterminate, onChange, label }) => {
  const ref = useRef(null);
  // React has no `indeterminate` prop — it is a DOM-only checkbox state, set
  // imperatively, the same way any other framework has to.
  useEffect(() => { if (ref.current) ref.current.indeterminate = !!indeterminate && !checked; }, [indeterminate, checked]);
  return (
    <input ref={ref} type="checkbox" checked={checked} aria-label={label} title={label}
      onClick={(e) => e.stopPropagation()} onChange={onChange}
      style={{ width: 14, height: 14, margin: 0, cursor: 'pointer', flexShrink: 0, accentColor: B.navy }} />
  );
};

const WELL_KNOWN_ORDER = ['inbox', 'drafts', 'sentitems', 'junkemail', 'archive', 'deleteditems', 'outbox'];
const WELL_KNOWN_ICON = {
  inbox: 'emailInbox', drafts: 'edit', sentitems: 'send', junkemail: 'warning',
  archive: 'archive', deleteditems: 'trash', outbox: 'mail',
};
const WELL_KNOWN_LABEL = {
  inbox: 'Inbox', drafts: 'Drafts', sentitems: 'Sent Items', junkemail: 'Junk Email',
  archive: 'Archive', deleteditems: 'Deleted Items', outbox: 'Outbox',
};

// The "Scheduled" row in the sidebar is not a real Graph folder — it is a
// SENTINEL id, recognized wherever the page decides what to load/render, so
// the rest of the folder-handling code (rename/delete/move) never has to know
// it exists. Never sent to the API as a folderId.
const SCHEDULED_SENTINEL_ID = '__scheduled__';

function orderedFolders(folders, wellKnown) {
  const idToKey = new Map();
  for (const [key, id] of Object.entries(wellKnown || {})) idToKey.set(id, key);
  const known = [];
  const custom = [];
  for (const f of (folders || [])) {
    const wk = idToKey.get(f.id);
    if (wk) known.push({ ...f, wellKnownKey: wk });
    else custom.push(f);
  }
  known.sort((a, b) => WELL_KNOWN_ORDER.indexOf(a.wellKnownKey) - WELL_KNOWN_ORDER.indexOf(b.wellKnownKey));
  custom.sort((a, b) => a.path.localeCompare(b.path));
  return [...known, ...custom];
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

function addressLine(list) {
  return (list || []).map((a) => (a.name && a.name !== a.address ? `${a.name} <${a.address}>` : a.address)).join(', ');
}

// Every API call that touches a mailbox threads `?mailbox=<email>` through
// when a SHARED mailbox is the active one (null/undefined → the caller's own
// mailbox, no param at all — the ordinary case, unchanged from before shared
// mailboxes existed).
function withMailbox(path, mailboxEmail) {
  if (!mailboxEmail) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}mailbox=${encodeURIComponent(mailboxEmail)}`;
}

// Microsoft's own Graph docs say it outright: "always URL encode the id" —
// a message/event id can contain characters (a literal `/`, a trailing `=`)
// that are not safe left raw in a request path. Every path this page builds
// with a message id goes through here rather than trusting it to already be
// URL-safe, which is what produced a 404 on an ordinary "mark as read".
const msgPath = (id, suffix = '') => `mail/messages/${encodeURIComponent(id)}${suffix}`;

// Both filters and the active mailbox are remembered per browser — "my
// default view" without a server-side preferences row. Read once at module
// load; a private window or blocked storage degrades to the ordinary
// built-in defaults rather than throwing.
const MAIL_PREFS_KEY = 'harvus.mail.prefs';
function loadMailPrefs() {
  try {
    const raw = window.localStorage.getItem(MAIL_PREFS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      unreadOnly: parsed.unreadOnly !== undefined ? !!parsed.unreadOnly : true,
      flaggedOnly: !!parsed.flaggedOnly,
      sort: typeof parsed.sort === 'string' ? parsed.sort : 'date',
      sortDir: parsed.sortDir === 'asc' ? 'asc' : 'desc',
    };
  } catch { return { unreadOnly: true, flaggedOnly: false, sort: 'date', sortDir: 'desc' }; }
}
function saveMailPrefs(prefs) {
  try { window.localStorage.setItem(MAIL_PREFS_KEY, JSON.stringify(prefs)); } catch { /* best-effort */ }
}

// Conversation threading (groupByConversation) and the bulk-action
// concurrency helper (runInChunks) are pure and live in
// utils/mailThreads.js, imported above — see that module for the reasoning.

// A message's HTML body is somebody else's content. Rendering it inline with
// dangerouslySetInnerHTML would run whatever script it carries in OUR origin.
// A sandboxed iframe with no `allow-scripts` cannot execute script at all,
// whatever the markup contains — `allow-same-origin` is present only so this
// component can measure the rendered height, not to permit anything the
// content could do.
const SafeHtmlFrame = ({ html }) => {
  const ref = useRef(null);
  const [height, setHeight] = useState(160);
  const onLoad = () => {
    try {
      const doc = ref.current?.contentDocument;
      if (doc?.body) setHeight(Math.min(Math.max(doc.body.scrollHeight + 24, 120), 5000));
    } catch { /* keep the current height */ }
  };
  return (
    <iframe
      ref={ref}
      title="Message body"
      sandbox="allow-same-origin"
      srcDoc={html || '<p></p>'}
      onLoad={onLoad}
      style={{ width: '100%', height, border: 'none', background: B.white, display: 'block' }}
    />
  );
};

// ── A small dropdown menu (flag menu, "More", folder actions) ────────────────
// Closes on an outside click and on Escape; every item is a real button.
const PopMenu = ({ trigger, items, align = 'right', width = 220 }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      {trigger({ open, toggle: () => setOpen((v) => !v) })}
      {open && (
        <div role="menu" style={{
          position: 'absolute', top: '100%', [align]: 0, marginTop: 4, zIndex: 40, width,
          background: B.white, border: `1px solid ${B.border}`, borderRadius: radius.lg,
          boxShadow: '0 4px 16px rgba(0,0,0,0.14)', padding: 4,
        }}>
          {items.filter(Boolean).map((it, i) => (it.divider ? (
            <div key={`d${i}`} style={{ borderTop: `1px solid ${B.divider}`, margin: '4px 0' }} />
          ) : (
            <button key={it.label} role="menuitem" disabled={it.disabled}
              onClick={(e) => { e.stopPropagation(); setOpen(false); it.onClick(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                padding: '7px 10px', border: 'none', background: 'transparent', borderRadius: radius.md,
                cursor: it.disabled ? 'default' : 'pointer', fontSize: font.sm, fontFamily: 'inherit',
                color: it.danger ? B.red : (it.disabled ? B.textLt : B.text),
              }}
              onMouseEnter={(e) => { if (!it.disabled) e.currentTarget.style.background = B.hover; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
              {it.icon && <Icon name={it.icon} size={13} />}
              <span style={{ flex: 1 }}>{it.label}</span>
              {it.hint && <span style={{ fontSize: font.xs, color: B.textLt }}>{it.hint}</span>}
            </button>
          )))}
        </div>
      )}
    </div>
  );
};

// Print a message. The body is somebody else's HTML, so it is printed from a
// hidden SANDBOXED iframe (no allow-scripts — the same rule SafeHtmlFrame
// follows); allow-modals is what lets the parent call print() on it.
function printMessage(detail) {
  const esc = (t) => String(t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const head = `<div style="font-family:Arial,sans-serif;font-size:12px;border-bottom:1px solid #ccc;padding-bottom:8px;margin-bottom:12px">
    <div style="font-size:16px;font-weight:bold">${esc(detail.subject)}</div>
    <div>From: ${esc(detail.from ? `${detail.from.name || ''} <${detail.from.address || ''}>` : '')}</div>
    <div>To: ${esc(addressLine(detail.to))}</div>
    ${detail.cc?.length ? `<div>Cc: ${esc(addressLine(detail.cc))}</div>` : ''}
    <div>Date: ${esc(detail.receivedAt ? new Date(detail.receivedAt).toLocaleString() : '')}</div></div>`;
  const body = detail.bodyType === 'html' ? detail.body : `<pre style="white-space:pre-wrap;font-family:inherit">${esc(detail.body)}</pre>`;
  const frame = document.createElement('iframe');
  frame.setAttribute('sandbox', 'allow-same-origin allow-modals');
  frame.style.cssText = 'position:fixed;width:0;height:0;border:0;right:0;bottom:0';
  frame.srcdoc = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(detail.subject)}</title></head><body>${head}${body || ''}</body></html>`;
  frame.onload = () => {
    try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch { /* the browser refused; nothing to do */ }
    setTimeout(() => frame.remove(), 60000);
  };
  document.body.appendChild(frame);
}

function downloadBase64(name, contentType, contentBytes) {
  const link = document.createElement('a');
  link.href = `data:${contentType};base64,${contentBytes}`;
  link.download = name;
  link.click();
}

const CategoriesModal = ({ initial, onClose, onSave }) => {
  const [list, setList] = useState(initial || []);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const add = () => {
    const v = draft.trim();
    if (v && !list.some((c) => c.toLowerCase() === v.toLowerCase())) setList((l) => [...l, v]);
    setDraft('');
  };
  return (
    <Modal title="Categorize" onClose={onClose} width={420}>
      <div style={{ fontSize: font.xs, color: B.textMd, marginBottom: space.md }}>
        Categories are labels Outlook colours from your own category list. A new name here still applies — Outlook shows it without a colour until you add it there.
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: space.md }}>
        {list.length === 0 && <span style={{ fontSize: font.sm, color: B.textLt }}>No categories.</span>}
        {list.map((c) => <Chip key={c} onRemove={() => setList((l) => l.filter((x) => x !== c))}>{c}</Chip>)}
      </div>
      <Input label="Add a category" value={draft} onChange={setDraft} placeholder="e.g. Red category, Follow up, Riverbend"
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn loading={busy} onClick={async () => {
          const final = draft.trim() && !list.some((c) => c.toLowerCase() === draft.trim().toLowerCase()) ? [...list, draft.trim()] : list;
          setBusy(true);
          try { await onSave(final); } finally { setBusy(false); }
        }}>Save</Btn>
      </ModalFooter>
    </Modal>
  );
};

const HeadersModal = ({ data, onClose }) => (
  <Modal title="Message details" onClose={onClose} width={760}>
    {!data ? <Spinner label="Loading headers…" /> : (
      <>
        {data.internetMessageId && (
          <div style={{ fontSize: font.xs, color: B.textMd, marginBottom: space.sm }}>Message-ID: <code>{data.internetMessageId}</code></div>
        )}
        <div style={{ maxHeight: '60vh', overflow: 'auto', border: `1px solid ${B.border}`, borderRadius: radius.md, background: B.surfaceMuted }}>
          <pre style={{ margin: 0, padding: space.md, fontSize: 11, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {(data.headers || []).map((h) => `${h.name}: ${h.value}`).join('\n') || 'No headers returned for this message.'}
          </pre>
        </div>
      </>
    )}
    <ModalFooter>
      {data?.headers?.length > 0 && (
        <Btn variant="ghost" onClick={() => navigator.clipboard?.writeText((data.headers || []).map((h) => `${h.name}: ${h.value}`).join('\n'))}>Copy</Btn>
      )}
      <Btn onClick={onClose}>Close</Btn>
    </ModalFooter>
  </Modal>
);

const SignatureModal = ({ initial, onClose, onSave }) => {
  const [html, setHtml] = useState(initial?.html || '');
  const [useOnNew, setUseOnNew] = useState(initial?.useOnNew !== false);
  const [useOnReplies, setUseOnReplies] = useState(!!initial?.useOnReplies);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  return (
    <Modal title="Email signature" onClose={onClose} width={620}>
      <div style={{ fontSize: font.xs, color: B.textMd, marginBottom: space.md }}>
        Harvus keeps its own signature — Microsoft does not let other apps read the one saved in Outlook. It is added when you start a message here; you can still edit it before sending.
      </div>
      <NoteBodyEditor initialContent={html} minHeight={140} onChange={({ html: h }) => setHtml(h)} style={{ marginBottom: space.md }} />
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: font.sm, marginBottom: 6 }}>
        <input type="checkbox" checked={useOnNew} onChange={(e) => setUseOnNew(e.target.checked)} /> Add to new messages
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: font.sm }}>
        <input type="checkbox" checked={useOnReplies} onChange={(e) => setUseOnReplies(e.target.checked)} /> Add to replies and forwards
      </label>
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn loading={busy} onClick={async () => {
          setBusy(true);
          try { await onSave({ html, useOnNew, useOnReplies }); onClose(); }
          catch (e) { toast.error(e.message || 'Could not save the signature.'); }
          finally { setBusy(false); }
        }}>Save</Btn>
      </ModalFooter>
    </Modal>
  );
};

const ShortcutsModal = ({ onClose }) => (
  <Modal title="Keyboard shortcuts" onClose={onClose} width={420}>
    {MAIL_SHORTCUTS.map((sc) => (
      <div key={sc.action} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${B.divider}`, fontSize: font.sm }}>
        <span>{sc.label}</span>
        <span>{sc.keys.map((k) => <kbd key={k} style={{ border: `1px solid ${B.border}`, borderRadius: 4, padding: '1px 6px', marginLeft: 4, fontSize: 11, background: B.surfaceMuted }}>{k === 'ArrowDown' ? '↓' : k === 'ArrowUp' ? '↑' : k}</kbd>)}</span>
      </div>
    ))}
    <div style={{ fontSize: font.xs, color: B.textLt, marginTop: space.md }}>Shortcuts are ignored while you are typing in a field.</div>
    <ModalFooter><Btn onClick={onClose}>Close</Btn></ModalFooter>
  </Modal>
);

// ── Mailbox switcher ─────────────────────────────────────────────────────────
// "My Mailbox" plus one row per connected shared mailbox — the same shape
// real Outlook uses for a Full-Access shared mailbox in its own left nav.
// Selecting one re-scopes everything below it: folders, messages, compose
// "from" identity.
const MailboxSwitcher = ({ mailboxes, active, onSelect, onAdd, onRemove }) => (
  <div style={{ marginBottom: space.md }}>
    <button onClick={() => onSelect(null)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
        border: 'none', borderRadius: radius.lg, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
        background: !active ? B.navy : 'transparent', color: !active ? '#fff' : B.text,
        fontSize: font.sm, fontWeight: weight.semibold,
      }}>
      <Icon name="mail" size={13} /> My Mailbox
    </button>
    {mailboxes.map((mb) => (
      <div key={mb.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button onClick={() => onSelect(mb.email)} title={mb.email}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', minWidth: 0,
            border: 'none', borderRadius: radius.lg, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
            background: active === mb.email ? B.navy : 'transparent', color: active === mb.email ? '#fff' : B.text,
            fontSize: font.sm, fontWeight: active === mb.email ? weight.semibold : weight.regular,
          }}>
          <Icon name="mail" size={13} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {mb.displayName || mb.email}
          </span>
        </button>
        <button title="Remove this shared mailbox" onClick={() => onRemove(mb)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: B.textLt, padding: 2, flexShrink: 0 }}>
          <Icon name="trash" size={11} />
        </button>
      </div>
    ))}
    <button onClick={onAdd}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', marginTop: 2,
        border: 'none', borderRadius: radius.lg, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
        background: 'transparent', color: B.textMd, fontSize: font.xs,
      }}>
      <Icon name="add" size={12} /> Add shared mailbox
    </button>
  </div>
);

const AddMailboxModal = ({ onClose, onAdd }) => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const submit = async () => {
    setBusy(true);
    try { await onAdd(email); onClose(); }
    catch (e) { toast.error(e.message || 'Could not add that mailbox.'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Add a shared mailbox" onClose={onClose} width={420}>
      <div style={{ fontSize: font.sm, color: B.textMd, marginBottom: space.md }}>
        You need Full Access to this mailbox in Exchange already — Harvus checks it against your own
        Microsoft connection rather than granting anything new.
      </div>
      <Input label="Mailbox email address" value={email} onChange={setEmail} placeholder="deals@harvus.com"
        autoFocus onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} loading={busy} disabled={!email.trim()}>Add</Btn>
      </ModalFooter>
    </Modal>
  );
};

// ── Message list ─────────────────────────────────────────────────────────────
// A quick-action icon (mark read/unread, delete) never opens the message — it
// stops the row's own click from firing. Both are opacity-toggled on the same
// hover state as the row's background, reserving their layout space always so
// nothing shifts when they appear (the pages/Documents.jsx bulk-select
// convention, applied here to per-row single-message actions instead).
const quickBtnStyle = {
  background: 'none', border: 'none', cursor: 'pointer', color: B.textLt, padding: 3,
  display: 'flex', alignItems: 'center', borderRadius: radius.sm,
};

// variant="ghost" defaults to a WHITE background — overriding only `color`
// (as the bulk toolbar below used to) leaves white-on-white, invisible on the
// navy bar. Override background + border too, per RealEstateBudgetTab.jsx's
// existing convention for a ghost button on a dark background.
// The selection toolbar sits on navy, so its buttons are NOT <Btn>: Btn's ghost
// variant repaints itself white on hover and restores its OWN white base on
// leave, which left an invisible white-on-white blob after the first hover.
// Icon-only (with titles) so all seven controls fit the 340px list column.
const ToolbarBtn = ({ icon, title, onClick, disabled }) => {
  const [hover, setHover] = useState(false);
  return (
    <button type="button" title={title} aria-label={title} disabled={disabled} onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        width: 28, height: 28, flexShrink: 0, padding: 0, borderRadius: radius.md,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: hover && !disabled ? 'rgba(255,255,255,.22)' : 'rgba(255,255,255,.08)',
        color: '#fff', border: '1px solid rgba(255,255,255,.35)',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
        transition: 'background 0.15s',
      }}>
      <Icon name={icon} size={14} />
    </button>
  );
};

// Harvi's verdict on the thread (the daily brief's reading, stored in
// dbo.MailThreadVerdicts). The label, tone and hover text come from
// @harvus/shared so the phone badges the same thread the same way.
const VERDICT_TONE_COLOR = { urgent: B.red, attention: B.amber, soon: B.navy, quiet: B.textMd };
const HarviVerdictChip = ({ verdict }) => {
  const chip = mailVerdictChip(verdict);
  if (!chip) return null;
  const color = VERDICT_TONE_COLOR[chip.tone] || B.textMd;
  return (
    <>
      <span title={chip.title} style={{
        fontSize: 10, padding: '0 6px', borderRadius: radius.pill, fontWeight: weight.semibold,
        color, border: `1px ${chip.stale ? 'dashed' : 'solid'} ${color}`, background: B.surface,
        opacity: chip.stale ? 0.75 : 1,
      }}>{chip.label}</span>
      {verdict.dealName && (
        <span title={`Harvi placed this thread on ${verdict.dealName}`} style={{
          fontSize: 10, padding: '0 6px', borderRadius: radius.pill, color: B.navy,
          background: B.surfaceMuted, border: `1px solid ${B.border}`,
        }}>{verdict.dealName}</span>
      )}
    </>
  );
};

const MessageRow = ({
  m, active, onClick, hovered, onMouseEnter, onMouseLeave,
  selected, onToggleSelect, showCheckbox,
  onQuickToggleRead, onQuickDelete,
  expandToggle, threadCount, indent = false, folderLabel = null,
}) => (
  <div onClick={onClick} role="button" tabIndex={0}
    onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    style={{
      display: 'flex', alignItems: 'flex-start', gap: 6,
      padding: indent ? '8px 12px 8px 34px' : '10px 12px',
      borderBottom: `1px solid ${B.divider}`, cursor: 'pointer',
      background: active ? B.tealLt : (hovered ? B.hover : (m.isRead ? 'transparent' : B.surfaceMuted)),
    }}>
    {!indent && (
      <div style={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center', paddingTop: 2 }}>
        {expandToggle || null}
      </div>
    )}
    <div style={{ width: 16, flexShrink: 0, paddingTop: 2, opacity: showCheckbox ? 1 : 0, pointerEvents: showCheckbox ? 'auto' : 'none' }}>
      <Tick checked={selected} onChange={() => onToggleSelect(m.id)}
        label={`Select message from ${(m.from && (m.from.name || m.from.address)) || 'unknown sender'}`} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: font.sm, fontWeight: m.isRead ? weight.regular : weight.bold, color: B.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {m.from ? (m.from.name || m.from.address) : '(unknown sender)'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          <button title={m.isRead ? 'Mark unread' : 'Mark read'} onClick={(e) => { e.stopPropagation(); onQuickToggleRead(); }}
            style={{ ...quickBtnStyle, opacity: hovered ? 1 : 0, pointerEvents: hovered ? 'auto' : 'none' }}>
            <Icon name="mail" size={12} />
          </button>
          <button title="Delete" onClick={(e) => { e.stopPropagation(); onQuickDelete(); }}
            style={{ ...quickBtnStyle, opacity: hovered ? 1 : 0, pointerEvents: hovered ? 'auto' : 'none' }}>
            <Icon name="trash" size={12} />
          </button>
          <span style={{ fontSize: font.xs, color: B.textLt, marginLeft: 2, whiteSpace: 'nowrap' }}>{m.receivedAt ? fmt.dateTime(m.receivedAt) : ''}</span>
        </div>
      </div>
      <div style={{ fontSize: font.sm, fontWeight: m.isRead ? weight.regular : weight.semibold, color: B.text, marginTop: 1, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        {m.importance === 'high' && <span title="High importance" style={{ color: B.red, fontWeight: weight.bold, flexShrink: 0 }}>!</span>}
        {m.flagged && <Icon name="flag" size={11} color={B.red} style={{ flexShrink: 0 }} />}
        {m.flagStatus === 'complete' && <Icon name="check" size={11} color={B.green} style={{ flexShrink: 0 }} />}
        {m.isDraft && <Chip color={B.amber} style={{ fontSize: 10, padding: '1px 6px', flexShrink: 0 }}>Draft</Chip>}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{m.subject}</span>
        {threadCount > 1 && <span style={{ fontSize: font.xs, color: B.textLt, flexShrink: 0 }}>({threadCount})</span>}
        {folderLabel && <span style={{ fontSize: 10, color: B.textLt, flexShrink: 0, marginLeft: 'auto' }}>{folderLabel}</span>}
      </div>
      <div style={{ fontSize: font.xs, color: B.textMd, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
        {m.hasAttachments && <Icon name="attach" size={11} style={{ marginRight: 4, verticalAlign: -1 }} />}
        {m.preview}
      </div>
      {(m.categories?.length > 0 || (m.flagged && m.flagDue) || m.harvi) && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3 }}>
          {m.harvi && <HarviVerdictChip verdict={m.harvi} />}
          {m.flagged && m.flagDue && (() => {
            const due = flagDueLabel(m.flagDue);
            return due ? <span style={{ fontSize: 10, color: due.overdue ? B.red : B.textMd, fontWeight: weight.semibold }}>{due.text}</span> : null;
          })()}
          {(m.categories || []).slice(0, 3).map((c) => (
            <span key={c} style={{ fontSize: 10, padding: '0 6px', borderRadius: radius.pill, background: B.surfaceMuted, border: `1px solid ${B.border}`, color: B.textMd }}>{c}</span>
          ))}
        </div>
      )}
    </div>
  </div>
);

// A conversation with more than one message on this page collapses to one
// row (the newest message) with a chevron + count; expanding reveals the
// rest, oldest-after-newest, each independently openable/selectable/
// deletable exactly like a standalone message.
const ThreadRow = ({
  group, activeId, hoveredId, setHoveredId, selectedIds, expanded, onToggleExpand,
  onOpen, onToggleSelect, showCheckbox, onQuickToggleRead, onQuickDelete, folderLabelOf,
}) => {
  const items = group.items;
  const isThread = items.length > 1;
  const top = items[0];

  const rowProps = (m) => ({
    m,
    active: m.id === activeId,
    onClick: () => onOpen(m),
    hovered: hoveredId === m.id,
    onMouseEnter: () => setHoveredId(m.id),
    onMouseLeave: () => setHoveredId((h) => (h === m.id ? null : h)),
    selected: selectedIds.has(m.id),
    onToggleSelect,
    showCheckbox: showCheckbox || hoveredId === m.id || selectedIds.has(m.id),
    onQuickToggleRead: () => onQuickToggleRead(m),
    onQuickDelete: () => onQuickDelete(m),
    folderLabel: folderLabelOf ? folderLabelOf(m) : null,
  });

  if (!isThread) return <MessageRow {...rowProps(top)} />;

  return (
    <div>
      <MessageRow {...rowProps(top)} threadCount={items.length}
        expandToggle={
          <button
            onClick={(e) => { e.stopPropagation(); onToggleExpand(group.key); }}
            title={expanded ? 'Collapse thread' : `Expand thread (${items.length} messages)`}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: B.textLt, padding: 0, display: 'flex' }}>
            <Icon name={expanded ? 'chevronDown' : 'chevronRight'} size={13} />
          </button>
        } />
      {expanded && items.slice(1).map((m) => <MessageRow key={m.id} {...rowProps(m)} indent />)}
    </div>
  );
};

// ── Recipient field — a plain address list, with a Harvus + Outlook contacts
// autocomplete for the segment currently being typed ────────────────────────
// THE VALUE STAYS A PLAIN COMMA-SEPARATED STRING — exactly what the API has
// always taken for to/cc/bcc, and exactly what a person can still type or
// paste by hand. This only adds a dropdown that fills in the address for
// whatever's after the last comma; it never changes what gets submitted, so
// typing an address nobody has ever heard of still works precisely as before.
const CONTACT_QUERY_MIN = 2;
const CONTACT_DEBOUNCE_MS = 200;

const RecipientField = ({ label, value, onChange, placeholder }) => {
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const currentTerm = (v) => {
    const parts = v.split(',');
    return parts[parts.length - 1].trim();
  };

  const runSearch = (term) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (term.length < CONTACT_QUERY_MIN) { setMatches([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`mail/contacts?q=${encodeURIComponent(term)}`);
        const list = r.contacts || [];
        setMatches(list);
        setOpen(list.length > 0);
        setHighlight(0);
      } catch {
        // Best-effort — the field is still a plain address box either way.
        setMatches([]); setOpen(false);
      }
    }, CONTACT_DEBOUNCE_MS);
  };

  const handleChange = (v) => {
    onChange(v);
    runSearch(currentTerm(v));
  };

  const pick = (c) => {
    const parts = value.split(',').map((p) => p.trim()).filter(Boolean);
    parts.pop(); // the in-progress term this suggestion replaces
    parts.push(c.name ? `${c.name} <${c.email}>` : c.email);
    onChange(`${parts.join(', ')}, `);
    setMatches([]); setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || !matches.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => (h + 1) % matches.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => (h - 1 + matches.length) % matches.length); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(matches[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <Input label={label} value={value} onChange={handleChange} placeholder={placeholder} onKeyDown={onKeyDown} />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: -8,
          background: B.white, border: `1px solid ${B.border}`, borderRadius: radius.lg,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto',
        }}>
          {matches.map((c, i) => (
            <div key={`${c.source}-${c.email}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => pick(c)}
              style={{
                padding: '7px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                background: i === highlight ? B.tealLt : 'transparent',
              }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: font.sm, fontWeight: weight.medium, color: B.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.name || c.email}
                </div>
                <div style={{ fontSize: font.xs, color: B.textLt, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.name ? c.email : (c.company || '')}
                </div>
              </div>
              <Chip style={{ fontSize: 10, padding: '1px 6px', flexShrink: 0 }}>
                {c.source === 'outlook' ? 'Outlook' : 'Harvus'}
              </Chip>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Schedule send picker ─────────────────────────────────────────────────────
// The same quick-pick shape real Outlook/OWA offers for "Send Later": a
// handful of presets plus a custom date/time — never a bare text box, which
// is how somebody types a typo'd date and it schedules for the wrong day.
function schedulePresets() {
  const now = new Date();
  const at = (h, addDays = 0) => {
    const d = new Date(now); d.setDate(d.getDate() + addDays); d.setHours(h, 0, 0, 0);
    return d;
  };
  const evening = now.getHours() < 18 ? at(18) : at(18, 1);
  return [
    { label: 'In 1 hour', date: new Date(now.getTime() + 60 * 60 * 1000) },
    { label: 'This evening (6 PM)', date: evening },
    { label: 'Tomorrow morning (8 AM)', date: at(8, 1) },
    { label: 'Tomorrow afternoon (1 PM)', date: at(13, 1) },
  ];
}

function toLocalDateTimeInputValue(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const SchedulePicker = ({ onClose, onSchedule }) => {
  const [custom, setCustom] = useState('');
  return (
    <div style={{
      position: 'absolute', bottom: '100%', right: 0, marginBottom: 6, zIndex: 30,
      background: B.white, border: `1px solid ${B.border}`, borderRadius: radius.lg,
      boxShadow: '0 4px 16px rgba(0,0,0,0.16)', padding: space.sm, width: 240,
    }}>
      <div style={{ fontSize: font.xs, fontWeight: weight.semibold, color: B.textMd, padding: '2px 6px 6px' }}>Schedule send</div>
      {schedulePresets().map((p) => (
        <button key={p.label} onClick={() => onSchedule(p.date.toISOString())}
          style={{
            display: 'block', width: '100%', textAlign: 'left', padding: '7px 8px', border: 'none',
            background: 'transparent', cursor: 'pointer', borderRadius: radius.md, fontSize: font.sm,
            color: B.text, fontFamily: 'inherit',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = B.hover; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
          {p.label}
        </button>
      ))}
      <div style={{ borderTop: `1px solid ${B.divider}`, marginTop: 6, paddingTop: 6 }}>
        <Input type="datetime-local" value={custom} onChange={setCustom}
          min={toLocalDateTimeInputValue(new Date(Date.now() + 60000))} />
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <Btn size="sm" variant="ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</Btn>
          <Btn size="sm" onClick={() => custom && onSchedule(new Date(custom).toISOString())}
            disabled={!custom} style={{ flex: 1 }}>Schedule</Btn>
        </div>
      </div>
    </div>
  );
};

// ── Compose modal (new / reply / reply-all / forward / resume a draft) ──────
// `mode: 'draft'` is "continue editing a message that's already a Graph
// draft" — resumed from clicking one in the Drafts folder (see openMessage
// below, which jumps straight here instead of a read-only preview, matching
// what Outlook does with a draft). `seed.draftId` is the SAME message id
// throughout: saving PUTs it in place (mailMessagePatch) and sending POSTs
// mail/messages/{id}/send — neither creates a second message.
//
// SEND ITSELF DOES NOT CALL THE API — clicking Send hands the built fields to
// `onSendNow`, which the page wraps in the undo-send delay. Only Save-as-draft
// and Schedule send call the API directly here, because neither needs (or, for
// Schedule, already has its own longer) undo window.

const ComposeModal = ({ mode, seed, mailboxEmail, signature, onClose, onSendNow, onScheduled }) => {
  const { toast } = useToast();
  const [to, setTo] = useState(seed.to || '');
  const [cc, setCc] = useState(seed.cc || '');
  const [bcc, setBcc] = useState(seed.bcc || '');
  const [subject, setSubject] = useState(seed.subject || '');
  // The signature is inserted ONCE, into a message that starts empty — never
  // into a resumed draft (it is already there) or an Undo'd send.
  const [bodyHtml, setBodyHtml] = useState(() => {
    if (seed.body) return seed.body;
    const wants = signature && signature.html
      && ((mode === 'new' && signature.useOnNew) || (mode !== 'new' && mode !== 'draft' && signature.useOnReplies));
    return wants ? `<p></p><p></p>${signature.html}` : '';
  });
  const [importance, setImportance] = useState(seed.importance || 'normal');
  const [readReceipt, setReadReceipt] = useState(!!seed.readReceipt);
  const [deliveryReceipt, setDeliveryReceipt] = useState(!!seed.deliveryReceipt);
  const [attachments, setAttachments] = useState(seed.attachments || []); // { name, contentType, contentBytes, size }
  const [busy, setBusy] = useState(false);
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);
  const fileInputRef = useRef(null);

  const isReplyLike = mode === 'reply' || mode === 'replyAll';
  const isForward = mode === 'forward';
  const isDraft = mode === 'draft';
  // Attachments are only ever wired up for a fresh compose — reply/forward's
  // Graph endpoints don't accept them, and resuming a draft has no Graph
  // sub-resource call built for adding one after the fact (see mailClient.js
  // — "SMALL FILES ONLY, INLINE, v1"). Hiding the control here is honest
  // about that rather than collecting files that would be silently dropped.
  const showAttach = mode === 'new';
  const title = mode === 'new' ? 'New message' : isDraft ? 'Edit draft'
    : mode === 'forward' ? 'Forward' : mode === 'replyAll' ? 'Reply all' : 'Reply';

  // BlockNote's uploadFile contract: return the URL to use for the dropped/
  // pasted image. No blob-upload endpoint exists for mail, so the image
  // becomes a data: URI directly in the HTML body — no server round trip,
  // and it rides inside the API's existing MAX_BODY_CHARS ceiling on the
  // whole message, so an oversized one is refused here with a friendly
  // message rather than surfacing as a generic "body too long" after send.
  const uploadInlineImage = async (file) => {
    if (!file.type || !file.type.startsWith('image/')) {
      toast.error('Only images can be inserted inline — attach anything else as a file instead.');
      throw new Error('Unsupported inline file type');
    }
    if (file.size > MAX_INLINE_IMAGE_BYTES) {
      toast.error(`"${file.name}" is larger than ${Math.round(MAX_INLINE_IMAGE_BYTES / 1024)} KB — insert a smaller image, or attach it as a file instead.`);
      throw new Error('Inline image too large');
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error || new Error('Could not read the image'));
      reader.onload = () => resolve(String(reader.result || ''));
      reader.readAsDataURL(file);
    });
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const f of files) {
      if (f.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`"${f.name}" is larger than ${Math.round(MAX_ATTACHMENT_BYTES / 1024 / 1024)} MB — attachments that big are not supported yet.`);
        continue;
      }
      try {
        const contentBytes = await fileToBase64(f);
        setAttachments((prev) => [...prev, { name: f.name, contentType: f.type || 'application/octet-stream', contentBytes, size: f.size }]);
      } catch { toast.error(`Could not read "${f.name}".`); }
    }
  };

  const fields = () => ({ to, cc, bcc, subject, bodyHtml, attachments, importance, readReceipt, deliveryReceipt });

  // Hands the built message off to the page's undo-send wrapper — no network
  // call happens here at all.
  const sendNow = () => {
    onSendNow(mode, seed, fields());
    onClose();
  };

  const scheduleSend = async (sendAtIso) => {
    setBusy(true);
    try {
      await onScheduled(mode, seed, fields(), sendAtIso);
      onClose();
    } catch (e) { toast.error(e.message || 'Could not schedule that.'); }
    finally { setBusy(false); }
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      if (mode === 'new') {
        await api.post(withMailbox('mail/drafts', mailboxEmail), { to, cc, bcc, subject, body: bodyHtml, bodyType: 'html', attachments, importance, readReceipt, deliveryReceipt });
        toast.success('Saved to Drafts');
      } else if (isDraft) {
        await api.put(withMailbox(msgPath(seed.draftId), mailboxEmail), { to, cc, bcc, subject, body: bodyHtml, bodyType: 'html', importance });
        toast.success('Draft updated');
      } else if (isReplyLike) {
        // send:false stops createReply's draft short of transmitting it —
        // it's already a real message sitting in Drafts once this returns.
        await api.post(withMailbox(msgPath(seed.messageId, '/reply'), mailboxEmail), { body: bodyHtml, bodyType: 'html', replyAll: mode === 'replyAll', send: false });
        toast.success('Saved to Drafts');
      } else {
        await api.post(withMailbox(msgPath(seed.messageId, '/forward'), mailboxEmail), { to, comment: bodyHtml, bodyType: 'html', send: false });
        toast.success('Saved to Drafts');
      }
      onClose();
    } catch (e) { toast.error(e.message || 'Could not save that draft.'); }
    finally { setBusy(false); }
  };

  // INLINE, NOT A POPUP — rendered by the page in place of the reading pane
  // (the third column), the same way an opened message already shows there.
  // A dialog on top of everything hides the very list the person is composing
  // in reply to; this is one continuous surface, closer to how Outlook's own
  // reading-pane reply works.
  return (
    <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.md }}>
        <div style={{ fontSize: font.lg, fontWeight: weight.bold, color: B.text }}>{title}</div>
        <button onClick={onClose} title="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: B.textLt, padding: 4 }}>
          <Icon name="close" size={16} />
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {(isReplyLike || isForward) && (
          <div style={{ fontSize: font.sm, color: B.textMd, marginBottom: space.md }}>
            Re: <strong style={{ color: B.text }}>{seed.subject}</strong>
            {seed.originalFrom ? ` — from ${seed.originalFrom}` : ''}
          </div>
        )}
        {(mode === 'new' || isDraft || mode === 'forward') && <RecipientField label="To" value={to} onChange={setTo} placeholder="name@company.com, name2@company.com" />}
        {(mode === 'new' || isDraft) && <RecipientField label="Cc" value={cc} onChange={setCc} placeholder="optional" />}
        {(mode === 'new' || isDraft) && <RecipientField label="Bcc" value={bcc} onChange={setBcc} placeholder="optional" />}
        {(mode === 'new' || isDraft) && <Input label="Subject" value={subject} onChange={setSubject} />}

        <label style={{ display: 'block', fontSize: font.sm, fontWeight: weight.medium, color: B.textMd, marginBottom: 6 }}>
          {isReplyLike ? 'Reply' : isForward ? 'Comment (optional)' : 'Message'}
        </label>
        <NoteBodyEditor
          initialContent={bodyHtml}
          minHeight={180}
          autoFocus
          uploadFile={uploadInlineImage}
          onChange={({ html }) => setBodyHtml(html)}
          style={{ marginBottom: space.sm }}
        />
        {isReplyLike && <div style={{ fontSize: font.xs, color: B.textLt, marginBottom: space.md }}>The original message is quoted below your reply automatically.</div>}

        {(mode === 'new' || isDraft) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.md, alignItems: 'center', marginBottom: space.md, fontSize: font.sm, color: B.textMd }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Importance
              <select value={importance} onChange={(e) => setImportance(e.target.value)}
                style={{ fontFamily: 'inherit', fontSize: font.sm, padding: '3px 6px', borderRadius: radius.md, border: `1px solid ${B.border}` }}>
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
              </select>
            </label>
            {mode === 'new' && (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={readReceipt} onChange={(e) => setReadReceipt(e.target.checked)} /> Request read receipt
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={deliveryReceipt} onChange={(e) => setDeliveryReceipt(e.target.checked)} /> Request delivery receipt
                </label>
              </>
            )}
          </div>
        )}

        {showAttach && (
          <div style={{ marginBottom: space.md }}>
            <input ref={fileInputRef} type="file" multiple onChange={onPickFiles} style={{ display: 'none' }} />
            <Btn size="sm" variant="ghost" onClick={() => fileInputRef.current?.click()}><Icon name="attach" size={13} /> Attach file</Btn>
            {attachments.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {attachments.map((a, i) => (
                  <Chip key={`${a.name}-${i}`} onRemove={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}>
                    {a.name} ({Math.round(a.size / 1024)} KB)
                  </Chip>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: `1px solid ${B.divider}`, paddingTop: space.md, marginTop: space.sm, flexShrink: 0 }}>
        <Btn variant="ghost" onClick={saveDraft} disabled={busy}>{isDraft ? 'Save' : 'Save as draft'}</Btn>
        <div style={{ position: 'relative', display: 'flex' }}>
          {schedulePickerOpen && (
            <SchedulePicker onClose={() => setSchedulePickerOpen(false)} onSchedule={scheduleSend} />
          )}
          <Btn variant="ghost" onClick={() => setSchedulePickerOpen((v) => !v)} disabled={busy}
            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 'none' }}
            title="Schedule send">
            <Icon name="chevronDown" size={12} />
          </Btn>
          <Btn onClick={sendNow} loading={busy} style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
            <Icon name="send" size={14} /> Send
          </Btn>
        </div>
      </div>
    </Card>
  );
};

// ── Move-to-folder modal ─────────────────────────────────────────────────────

const MoveModal = ({ folders, wellKnown, currentFolderId, onClose, onMove, title = 'Move to folder' }) => {
  const rows = useMemo(() => orderedFolders(folders, wellKnown).filter((f) => f.id !== currentFolderId), [folders, wellKnown, currentFolderId]);
  const [target, setTarget] = useState(rows[0]?.id || '');
  return (
    <Modal title={title} onClose={onClose} width={420}>
      <Select label="Destination" value={target} onChange={setTarget}
        options={rows.map((f) => ({ value: f.id, label: f.wellKnownKey ? (WELL_KNOWN_LABEL[f.wellKnownKey] || f.path) : f.path }))} />
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onMove(target)} disabled={!target}>Move</Btn>
      </ModalFooter>
    </Modal>
  );
};

const FolderNameModal = ({ title, initial = '', onClose, onSave }) => {
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const save = async () => {
    setBusy(true);
    try { await onSave(name); }
    catch (e) { toast.error(e.message || 'Could not save that.'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={title} onClose={onClose} width={380}>
      <Input label="Folder name" value={name} onChange={setName} autoFocus onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} loading={busy} disabled={!name.trim()}>Save</Btn>
      </ModalFooter>
    </Modal>
  );
};

// ── Scheduled sends list ─────────────────────────────────────────────────────
// Shown in place of the message list when the sidebar's "Scheduled" row is
// selected. Each row's subject/recipients are fetched lazily off its own
// still-a-draft message — the list itself only records WHEN each is due.

function timeUntil(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'sending now';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `in ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `in ${hours} hr`;
  return `in ${Math.round(hours / 24)} day${Math.round(hours / 24) === 1 ? '' : 's'}`;
}

const ScheduledRow = ({ s, mailboxEmail, onChanged }) => {
  const [preview, setPreview] = useState(null);
  const { toast } = useToast();
  useEffect(() => {
    let cancelled = false;
    api.get(withMailbox(msgPath(s.draftId), mailboxEmail))
      .then((d) => { if (!cancelled) setPreview(d); })
      .catch(() => { if (!cancelled) setPreview(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.id]);

  const cancel = async () => {
    try {
      await api.post(`mail/scheduled-sends/${s.id}/cancel`);
      toast.success('Undone — it will not be sent.');
      onChanged();
    } catch (e) { toast.error(e.message || 'Could not undo that.'); }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: `1px solid ${B.divider}` }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: font.sm, fontWeight: weight.medium, color: B.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {preview === null ? 'Loading…' : preview === false ? '(message unavailable)' : (preview.subject || '(no subject)')}
        </div>
        <div style={{ fontSize: font.xs, color: B.textLt }}>
          {preview && preview !== false ? `To: ${addressLine(preview.to) || '—'} — ` : ''}
          Sends {timeUntil(s.sendAt)} ({fmt.stamp(s.sendAt)})
          {s.status === 'error' && <span style={{ color: B.red }}> — could not be sent: {s.lastError || 'unknown error'}</span>}
        </div>
      </div>
      <Btn size="sm" variant="ghost" onClick={cancel}>Undo</Btn>
    </div>
  );
};

const ScheduledList = ({ scheduledSends, mailboxEmail, onChanged }) => (
  <div style={{ flex: 1, overflowY: 'auto', border: `1px solid ${B.border}`, borderRadius: radius.lg }}>
    {scheduledSends.length === 0 ? (
      <div style={{ padding: space.xl, textAlign: 'center', color: B.textMd, fontSize: font.sm }}>Nothing scheduled.</div>
    ) : scheduledSends.map((s) => <ScheduledRow key={s.id} s={s} mailboxEmail={mailboxEmail} onChanged={onChanged} />)}
  </div>
);

// ── Undo-send toast ──────────────────────────────────────────────────────────
// The whole of UNDO SEND: a countdown, and a button that cancels the timeout
// before it ever calls the API. See UNDO_SEND_SECONDS's comment above.
const UndoSendBanner = ({ secondsLeft, onUndo }) => (
  <div style={{
    position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 200,
    background: B.navy, color: '#fff', borderRadius: radius.lg, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
    padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 14, fontSize: font.sm,
  }}>
    <span>Sending in {secondsLeft}s…</span>
    <Btn size="sm" variant="ghost" onClick={onUndo} style={{ color: '#fff', fontWeight: weight.semibold }}>Undo</Btn>
  </div>
);

// ── Main page ────────────────────────────────────────────────────────────────

export const Mail = ({ setPage }) => {
  const { toast } = useToast();
  const { data: status, loading: statusLoading } = usePersonalConnectStatus();

  const [mailboxes, setMailboxes] = useState([]);
  const [activeMailbox, setActiveMailbox] = useState(null); // null = own mailbox; else a shared mailbox's email
  const [addMailboxOpen, setAddMailboxOpen] = useState(false);
  // /me-only, deliberately — Triage is the person's own workload, not a
  // shared mailbox's, so it has no dependency on activeMailbox at all.
  const [triageOpen, setTriageOpen] = useState(false);

  const [folders, setFolders] = useState([]);
  const [wellKnown, setWellKnown] = useState({});
  const [foldersLoaded, setFoldersLoaded] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);

  const [messages, setMessages] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [search, setSearch] = useState('');
  // The default view — unread mail, remembered per browser (loadMailPrefs) —
  // rather than always opening on every message in the Inbox.
  const [mailPrefs] = useState(loadMailPrefs);
  const [unreadOnly, setUnreadOnly] = useState(mailPrefs.unreadOnly);
  const [flaggedOnly, setFlaggedOnly] = useState(mailPrefs.flaggedOnly);
  const [sort, setSort] = useState(mailPrefs.sort);
  const [sortDir, setSortDir] = useState(mailPrefs.sortDir);
  // Focused Inbox tabs — 'all' by default, because on a mailbox that does not
  // use Focused Inbox a Focused-first default would look like missing mail.
  const [focus, setFocus] = useState('all');
  // Outlook's search scope: this folder, or every folder.
  const [searchAll, setSearchAll] = useState(false);
  useEffect(() => { saveMailPrefs({ unreadOnly, flaggedOnly, sort, sortDir }); }, [unreadOnly, flaggedOnly, sort, sortDir]);

  const [signature, setSignature] = useState(null);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [categorizing, setCategorizing] = useState(null); // a message, or null
  const [filingMsg, setFilingMsg] = useState(null); // a message being filed to a deal, or null
  const [headers, setHeaders] = useState(undefined); // undefined closed · null loading · object loaded
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Conversation threading (grouping only ever sees the messages already
  // loaded on this page — see groupByConversation above) and multi-select.
  const [expandedThreads, setExpandedThreads] = useState(() => new Set());
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const [compose, setCompose] = useState(null); // { mode, seed }
  const [movingIds, setMovingIds] = useState(null); // ids the open MoveModal targets, or null when closed
  const [newFolder, setNewFolder] = useState(false);
  const [renaming, setRenaming] = useState(null);

  // Scheduled sends — the sidebar's synthetic "Scheduled" row.
  const [scheduledSends, setScheduledSends] = useState([]);

  // Undo Send — see UndoSendBanner above. `pending` holds everything needed
  // to actually fire the send once the window elapses.
  const [pending, setPending] = useState(null); // { key, mode, seed, fields, deadline, mailboxEmail }
  const [secondsLeft, setSecondsLeft] = useState(0);

  const connected = !!status?.connected;
  const threadGroups = useMemo(() => groupByConversation(messages), [messages]);
  const onScheduledView = selectedFolder?.id === SCHEDULED_SENTINEL_ID;

  useEffect(() => {
    if (!connected) return;
    api.get('mail/signature').then(setSignature).catch(() => setSignature(null));
  }, [connected]);
  const saveSignature = async (value) => {
    const r = await api.put('mail/signature', value);
    setSignature(r);
    toast.success('Signature saved');
  };

  const loadMailboxes = useCallback(async () => {
    try {
      const r = await api.get('mail/shared-mailboxes');
      setMailboxes(r.mailboxes || []);
    } catch { /* best-effort — the switcher just shows "My Mailbox" alone */ }
  }, []);
  useEffect(() => { if (connected) loadMailboxes(); }, [connected, loadMailboxes]);

  // ── Loading ────────────────────────────────────────────────────────────────
  //
  // THE LIST NO LONGER WAITS FOR THE FOLDER TREE. It used to: the Inbox's id
  // came from the folders response, so the message list could not even be
  // asked for until the whole tree had been read. Graph accepts the
  // well-known name `inbox` as a folder id, so the page opens on an ALIAS
  // (`{ id: 'inbox', alias: true }`), fires the folder tree and the Inbox list
  // together, and swaps the alias for the real id when the tree lands —
  // without reloading the list it already has (aliasUpgradeRef).
  const aliasUpgradeRef = useRef(null);
  // Every list request carries a sequence number; a response that is not the
  // latest one is dropped, so a slow Inbox load cannot overwrite the folder
  // the person clicked while it was in flight.
  const loadSeqRef = useRef(0);
  // The delta-sync state for the list on screen: which view it belongs to and
  // the Graph deltaLink that says "changes since here".
  const syncRef = useRef({ key: null, deltaLink: null, seeding: false });

  const logTiming = (label, startedAt, r) => {
    const ms = Math.round(performance.now() - startedAt);
    // eslint-disable-next-line no-console
    console.info(formatTiming(label, ms, r && r.timing));
  };

  const applyFolders = (r) => {
    setFolders(r.folders || []);
    setWellKnown(r.wellKnown || {});
    setFoldersLoaded(true);
  };

  const loadFolders = useCallback(async () => {
    const started = performance.now();
    try {
      const r = await api.get(withMailbox('mail/folders', activeMailbox));
      logTiming('folders', started, r);
      mailViewCache.setFolders(activeMailbox, { folders: r.folders || [], wellKnown: r.wellKnown || {} });
      applyFolders(r);
      const inboxId = r.wellKnown?.inbox;
      setSelectedFolder((prev) => {
        // Only the opening alias is upgraded; a folder the person has already
        // clicked is theirs and stays selected.
        if (prev && prev.alias && inboxId) {
          aliasUpgradeRef.current = inboxId;
          return { id: inboxId, path: 'Inbox', wellKnownKey: 'inbox' };
        }
        return prev;
      });
    } catch (e) { toast.error(e.message || 'Could not load folders.'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMailbox]);

  // Switching mailboxes re-scopes everything — a folder id from one mailbox
  // means nothing in another's tree.
  useEffect(() => {
    if (!connected) return;
    setSelectedId(null); setDetail(null); setSelectedIds(new Set());
    syncRef.current = { key: null, deltaLink: null, seeding: false };
    // Paint from the in-memory cache when this mailbox was shown earlier in
    // the session; the fresh read below replaces it.
    const cached = mailViewCache.getFolders(activeMailbox);
    if (cached) {
      applyFolders(cached);
      setSelectedFolder(cached.wellKnown?.inbox
        ? { id: cached.wellKnown.inbox, path: 'Inbox', wellKnownKey: 'inbox' }
        : { id: 'inbox', path: 'Inbox', wellKnownKey: 'inbox', alias: true });
    } else {
      setFoldersLoaded(false); setFolders([]); setWellKnown({}); setMessages([]);
      setSelectedFolder({ id: 'inbox', path: 'Inbox', wellKnownKey: 'inbox', alias: true });
    }
    loadFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, activeMailbox]);

  const loadScheduledSends = useCallback(async () => {
    try {
      const r = await api.get('mail/scheduled-sends');
      setScheduledSends((r.scheduledSends || []).filter((s) => (s.mailboxEmail || null) === activeMailbox));
    } catch { /* best-effort */ }
  }, [activeMailbox]);

  const viewNow = () => ({ unreadOnly, flaggedOnly, focus, sort, sortDir });
  const deltaAllowed = (folderId) => canDeltaSync({ search, sort, sortDir, searchAll, folderId });

  // Seed the delta in the background once a list is on screen, so the next
  // Sync (and the auto-sync) asks Graph "what changed" instead of re-reading
  // the page. Invisible to the person; a failure just means Sync reloads.
  const seedSync = async (folderId, loaded, key) => {
    if (!deltaAllowed(folderId)) { syncRef.current = { key: null, deltaLink: null, seeding: false }; return; }
    syncRef.current = { key, deltaLink: null, seeding: true };
    const started = performance.now();
    try {
      let link = null;
      let r = await api.get(withMailbox(`mail/sync?folderId=${encodeURIComponent(folderId)}&since=${encodeURIComponent(syncSinceFor(loaded))}`, activeMailbox), { timeout: 60000 });
      // A seed larger than one server budget continues where it stopped.
      for (let i = 0; i < 5 && r && !r.unsupported && !r.deltaLink && r.nextLink; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        r = await api.get(withMailbox(`mail/sync?deltaLink=${encodeURIComponent(r.nextLink)}`, activeMailbox), { timeout: 60000 });
      }
      logTiming('sync seed', started, r);
      if (r && !r.unsupported) link = r.deltaLink || null;
      if (syncRef.current.key === key) syncRef.current = { key, deltaLink: link, seeding: false };
    } catch {
      if (syncRef.current.key === key) syncRef.current = { key, deltaLink: null, seeding: false };
    }
  };

  const loadMessages = useCallback(async (folderId, { append = false, pageToken = null } = {}) => {
    if (!folderId || folderId === SCHEDULED_SENTINEL_ID) return;
    const seq = ++loadSeqRef.current;
    const view = viewNow();
    const key = mailViewCache.listKey(activeMailbox, folderId, view);
    const plain = !append && !search.trim();
    // Paint the last copy of this exact view at once while the fresh read runs.
    if (plain) {
      const cachedList = mailViewCache.getList(key);
      if (cachedList) { setMessages(cachedList.messages); setNextPageToken(cachedList.nextPageToken); }
    }
    setLoadingMessages(true);
    const started = performance.now();
    try {
      // top: '50' — the server's own MAX_PAGE_SIZE, so the default view
      // reaches the "latest 100 unread" in one Load More click rather than
      // four.
      const params = new URLSearchParams({ folderId, top: '50' });
      if (search.trim()) params.set('search', search.trim());
      if (unreadOnly) params.set('unreadOnly', 'true');
      if (flaggedOnly) params.set('flaggedOnly', 'true');
      if (focus !== 'all') params.set('focus', focus);
      if (sort !== 'date' || sortDir !== 'desc') { params.set('sort', sort); params.set('sortDir', sortDir); }
      if (searchAll && search.trim()) params.set('scope', 'all');
      if (pageToken) params.set('pageToken', pageToken);
      const r = await api.get(withMailbox(`mail/messages?${params.toString()}`, activeMailbox));
      logTiming(append ? 'messages (more)' : 'messages', started, r);
      if (seq !== loadSeqRef.current && !append) return; // superseded
      const list = r.messages || [];
      setMessages((prev) => (append ? [...prev, ...list] : list));
      setNextPageToken(r.nextPageToken || null);
      if (plain) {
        mailViewCache.setList(key, { messages: list, nextPageToken: r.nextPageToken || null });
        seedSync(folderId, list, key);
      }
      // A filter or sort Graph could not honour is said, never silently lost.
      if (!append && r.droppedFilters?.length) {
        const names = r.droppedFilters.map((f) => ({ unreadOnly: 'Unread only', flaggedOnly: 'Flagged only', focus: 'Focused/Other', sort: 'the sort order' }[f] || f));
        toast.info(`Search ignores ${names.join(', ')} — Outlook cannot combine them.`);
      }
    } catch (e) { if (seq === loadSeqRef.current) toast.error(e.message || 'Could not load messages.'); }
    finally { if (seq === loadSeqRef.current || append) setLoadingMessages(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, unreadOnly, flaggedOnly, focus, sort, sortDir, searchAll, activeMailbox]);

  useEffect(() => {
    if (onScheduledView) { loadScheduledSends(); return; }
    if (selectedFolder) {
      // The alias → real-id swap is the SAME folder; the list already loaded
      // for `inbox` is the list for it, so nothing is cleared or re-read.
      if (aliasUpgradeRef.current && selectedFolder.id === aliasUpgradeRef.current) {
        aliasUpgradeRef.current = null;
        return;
      }
      aliasUpgradeRef.current = null;
      setSelectedId(null); setDetail(null);
      setSelectedIds(new Set()); setExpandedThreads(new Set()); setHoveredId(null);
      loadMessages(selectedFolder.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder?.id, activeMailbox, search, unreadOnly, flaggedOnly, focus, sort, sortDir, searchAll]);

  // A DRAFT jumps straight into the compose editor rather than a read-only
  // preview — matching what Outlook does when you click one in Drafts.
  // seed.draftId is the SAME Graph message id throughout the edit/save/send
  // cycle (see ComposeModal's `mode: 'draft'`).
  const openMessage = async (m) => {
    setSelectedId(m.id);
    setLoadingDetail(true);
    try {
      const d = await api.get(withMailbox(msgPath(m.id), activeMailbox));
      if (d.isDraft) {
        setCompose({
          mode: 'draft',
          seed: {
            draftId: d.id, to: addressLine(d.to), cc: addressLine(d.cc), bcc: addressLine(d.bcc),
            subject: d.subject, body: d.body, bodyType: d.bodyType, importance: d.importance,
          },
        });
        return;
      }
      setDetail(d);
      if (!m.isRead) setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, isRead: true } : x)));
    } catch (e) { toast.error(e.message || 'Could not open that message.'); }
    finally { setLoadingDetail(false); }
  };

  const refreshCurrentFolder = () => selectedFolder && !onScheduledView && loadMessages(selectedFolder.id);

  // Sync — ask Outlook what changed since the list was loaded (one delta
  // round trip, usually answering "nothing"), and re-read the folder tree for
  // its unread counts. Falls back to a full reload when the view cannot be
  // kept current by delta (a search, another sort) or the delta has expired.
  // Deliberately NOT loadFolders(): that would move the selection.
  const [syncing, setSyncing] = useState(false);
  const deltaSyncOnce = async ({ quiet = false } = {}) => {
    const folderId = selectedFolder?.id;
    if (!folderId || onScheduledView || !deltaAllowed(folderId)) return false;
    const key = mailViewCache.listKey(activeMailbox, folderId, viewNow());
    const st = syncRef.current;
    if (st.key !== key || !st.deltaLink) return false;
    const started = performance.now();
    let r = await api.get(withMailbox(`mail/sync?deltaLink=${encodeURIComponent(st.deltaLink)}`, activeMailbox), { timeout: 60000 });
    const changes = [...(r.changes || [])];
    for (let i = 0; i < 5 && r && !r.unsupported && !r.deltaLink && r.nextLink; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      r = await api.get(withMailbox(`mail/sync?deltaLink=${encodeURIComponent(r.nextLink)}`, activeMailbox), { timeout: 60000 });
      changes.push(...(r.changes || []));
    }
    logTiming(quiet ? 'auto-sync' : 'sync', started, r);
    if (!r || r.unsupported || !r.deltaLink) { syncRef.current = { key: null, deltaLink: null, seeding: false }; return false; }
    if (syncRef.current.key !== key) return true; // the view moved on meanwhile
    syncRef.current = { key, deltaLink: r.deltaLink, seeding: false };
    if (changes.length) {
      setMessages((prev) => {
        const out = applyMailDelta(prev, changes, {
          unreadOnly, flaggedOnly, focus, keepIds: [selectedId], windowComplete: !nextPageToken,
        }).messages;
        mailViewCache.setList(key, { messages: out, nextPageToken });
        return out;
      });
    }
    return true;
  };

  const syncMail = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const folderRead = (async () => {
        const started = performance.now();
        const r = await api.get(withMailbox('mail/folders', activeMailbox));
        logTiming('sync folders', started, r);
        mailViewCache.setFolders(activeMailbox, { folders: r.folders || [], wellKnown: r.wellKnown || {} });
        setFolders(r.folders || []);
        setWellKnown(r.wellKnown || {});
      })();
      if (onScheduledView) await loadScheduledSends();
      else if (selectedFolder) {
        let done = false;
        try { done = await deltaSyncOnce(); } catch { done = false; }
        if (!done) await loadMessages(selectedFolder.id);
      }
      await folderRead;
      loadScheduledSends();
    } catch (e) { toast.error(e.message || 'Could not sync mail.'); }
    finally { setSyncing(false); }
  };

  // AUTO-SYNC: every minute while the tab is visible, a delta round trip
  // brings in new mail and read/flag changes made in Outlook. It only ever
  // uses delta — it never falls back to a full reload behind the person's
  // back — and it skips while anything else is loading.
  const autoSyncRef = useRef(null);
  autoSyncRef.current = () => {
    if (syncing || loadingMessages || document.visibilityState !== 'visible') return;
    deltaSyncOnce({ quiet: true }).catch(() => {});
  };
  useEffect(() => {
    if (!connected) return undefined;
    const id = setInterval(() => autoSyncRef.current && autoSyncRef.current(), 60000);
    return () => clearInterval(id);
  }, [connected]);

  // Outlook's flag menu — a due date, Mark complete, or Clear.
  const setFlag = async (m, choice) => {
    const flag = { ...flagForChoice(choice), timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone };
    try {
      await api.put(withMailbox(msgPath(m.id), activeMailbox), { flag });
      const patch = {
        flagged: flag.status === 'flagged', flagStatus: flag.status,
        flagDue: flag.status === 'flagged' ? (flag.dueDate || null) : null,
      };
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, ...patch } : x)));
      if (detail?.id === m.id) setDetail((d) => ({ ...d, ...patch }));
    } catch (e) { toast.error(e.message || 'Could not update the flag.'); }
  };
  const flagMenuItems = (m) => [
    ...FLAG_CHOICES.map((c) => ({ label: c.label, icon: 'flag', onClick: () => setFlag(m, c.key) })),
    { divider: true },
    { label: 'Mark complete', icon: 'check', onClick: () => setFlag(m, 'complete') },
    { label: 'Clear flag', icon: 'close', onClick: () => setFlag(m, 'clear'), disabled: m.flagStatus === 'notFlagged' && !m.flagged },
  ];

  const saveCategories = async (m, categories) => {
    try {
      await api.put(withMailbox(msgPath(m.id), activeMailbox), { categories });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, categories } : x)));
      if (detail?.id === m.id) setDetail((d) => ({ ...d, categories }));
      setCategorizing(null);
    } catch (e) { toast.error(e.message || 'Could not save categories.'); }
  };

  const viewHeaders = async (m) => {
    setHeaders(null);
    try { setHeaders(await api.get(withMailbox(msgPath(m.id, '/headers'), activeMailbox))); }
    catch (e) { setHeaders(undefined); toast.error(e.message || 'Could not load the message details.'); }
  };
  const downloadEml = async (m) => {
    try {
      const r = await api.get(withMailbox(msgPath(m.id, '/mime'), activeMailbox));
      downloadBase64(r.name, r.contentType, r.contentBytes);
    } catch (e) { toast.error(e.message || 'Could not download that message.'); }
  };

  // Archive / Junk / Not junk are moves to a well-known folder. Graph takes
  // the well-known NAME as a destination directly, so a mailbox whose Archive
  // folder id we never resolved still archives.
  const moveToWellKnown = (key, ids) => doMove(wellKnown[key] || key, ids);
  const inJunk = selectedFolder && (selectedFolder.wellKnownKey === 'junkemail' || selectedFolder.id === wellKnown.junkemail);

  // Shared by the detail pane's own button, a row's hover quick-action, and
  // (through runInChunks) the bulk toolbar — one place decides what "mark
  // read/unread" means so the three can't drift.
  const quickToggleRead = async (m) => {
    const nextRead = !m.isRead;
    try {
      await api.put(withMailbox(msgPath(m.id), activeMailbox), { isRead: nextRead });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, isRead: nextRead } : x)));
      if (detail?.id === m.id) setDetail((d) => ({ ...d, isRead: nextRead }));
    } catch (e) { toast.error(e.message || 'Could not update that.'); }
  };
  const toggleUnread = () => detail && quickToggleRead(detail);

  // The single-message primitive every delete (detail pane, hover, bulk)
  // goes through. No confirm here — that's the caller's job, once, so a bulk
  // delete asks a single time for the whole batch rather than once per item.
  const deleteOne = async (id) => {
    await api.del(withMailbox(msgPath(id, `?folderId=${encodeURIComponent(selectedFolder?.id || '')}`), activeMailbox));
    setMessages((prev) => prev.filter((x) => x.id !== id));
    setSelectedIds((prev) => { if (!prev.has(id)) return prev; const next = new Set(prev); next.delete(id); return next; });
    if (detail?.id === id) { setDetail(null); setSelectedId(null); }
  };
  const quickDelete = async (m) => {
    if (!window.confirm('Delete this message?')) return;
    try { await deleteOne(m.id); toast.success('Deleted'); }
    catch (e) { toast.error(e.message || 'Could not delete that message.'); }
  };
  const doDelete = () => detail && quickDelete(detail);

  const doMove = async (targetFolderId, ids) => {
    const list = ids && ids.length ? ids : [];
    if (!list.length) return;
    setBulkBusy(true);
    const failed = [];
    await runInChunks(list, 4, async (id) => {
      try { await api.put(withMailbox(msgPath(id), activeMailbox), { move: targetFolderId }); }
      catch { failed.push(id); }
    });
    setBulkBusy(false);
    setMovingIds(null);
    setSelectedIds((prev) => { const next = new Set(prev); for (const id of list) next.delete(id); return next; });
    if (detail && list.includes(detail.id)) { setDetail(null); setSelectedId(null); }
    if (failed.length) toast.error(`Could not move ${failed.length} of ${list.length} message${list.length === 1 ? '' : 's'}.`);
    else toast.success(list.length > 1 ? `Moved ${list.length} messages` : 'Moved');
    refreshCurrentFolder();
  };

  // ── Multi-select bulk actions ──────────────────────────────────────────────
  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());

  // A three-state master checkbox — none / some (indeterminate) / all of the
  // currently LOADED messages, the same convention pages/Documents.jsx uses
  // for its own "select all" over one page of rows (a person selecting "all"
  // on a long folder almost certainly means what's on screen, not every
  // message across every future "Load more").
  const allIds = useMemo(() => messages.map((m) => m.id), [messages]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(allIds));

  const bulkMarkRead = async (read) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    const failed = [];
    await runInChunks(ids, 4, async (id) => {
      try {
        await api.put(withMailbox(msgPath(id), activeMailbox), { isRead: read });
        setMessages((prev) => prev.map((x) => (x.id === id ? { ...x, isRead: read } : x)));
        if (detail?.id === id) setDetail((d) => ({ ...d, isRead: read }));
      } catch { failed.push(id); }
    });
    setBulkBusy(false);
    if (failed.length) toast.error(`Could not update ${failed.length} of ${ids.length} message${ids.length === 1 ? '' : 's'}.`);
    else toast.success(read ? 'Marked as read' : 'Marked as unread');
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} message${ids.length === 1 ? '' : 's'}?`)) return;
    setBulkBusy(true);
    const failed = [];
    await runInChunks(ids, 4, async (id) => {
      try { await deleteOne(id); } catch { failed.push(id); }
    });
    setBulkBusy(false);
    if (failed.length) toast.error(`Could not delete ${failed.length} of ${ids.length} message${ids.length === 1 ? '' : 's'}.`);
    else toast.success(`Deleted ${ids.length} message${ids.length === 1 ? '' : 's'}`);
  };

  const openReply = (all) => {
    setCompose({
      mode: all ? 'replyAll' : 'reply',
      seed: { messageId: detail.id, subject: detail.subject, originalFrom: detail.from ? (detail.from.name || detail.from.address) : null },
    });
  };
  const openForward = () => {
    setCompose({ mode: 'forward', seed: { messageId: detail.id, subject: `Fwd: ${detail.subject}` } });
  };

  const createFolder = async (name) => {
    await api.post(withMailbox('mail/folders', activeMailbox), { name });
    setNewFolder(false);
    toast.success('Folder created');
    loadFolders();
  };
  const renameFolder = async (name) => {
    await api.put(withMailbox(`mail/folders/${renaming.id}`, activeMailbox), { name });
    setRenaming(null);
    toast.success('Renamed');
    loadFolders();
  };
  const deleteFolder = async (f) => {
    if (!window.confirm(`Delete the folder "${f.path}"? Its contents move to Deleted Items.`)) return;
    try {
      await api.del(withMailbox(`mail/folders/${f.id}`, activeMailbox));
      toast.success('Folder deleted');
      if (selectedFolder?.id === f.id) setSelectedFolder(null);
      loadFolders();
    } catch (e) { toast.error(e.message || 'Could not delete that folder.'); }
  };

  // Folder-wide actions repeat the bounded server call while it says there is
  // more (see mailFolderEmpty / mailFolderMarkRead in api/src/functions/mail.js).
  const runFolderAction = async (f, action) => {
    let total = 0;
    let failed = 0;
    for (let lap = 0; lap < 50; lap += 1) {
      // eslint-disable-next-line no-await-in-loop
      const r = await api.post(withMailbox(`mail/folders/${encodeURIComponent(f.id)}/${action}`, activeMailbox), {}, { timeout: 60000 });
      total += r.processed || 0;
      failed += r.failed || 0;
      if (!r.remaining || !(r.processed > 0)) break;
    }
    return { total, failed };
  };
  const markFolderRead = async (f) => {
    try {
      const { total, failed } = await runFolderAction(f, 'mark-read');
      toast.success(`Marked ${total} message${total === 1 ? '' : 's'} as read${failed ? ` (${failed} failed)` : ''}`);
      if (selectedFolder?.id === f.id) refreshCurrentFolder();
      loadFolders();
    } catch (e) { toast.error(e.message || 'Could not mark that folder as read.'); }
  };
  const emptyFolder = async (f) => {
    const key = f.wellKnownKey;
    const permanent = key === 'deleteditems' || key === 'junkemail';
    const label = key ? (WELL_KNOWN_LABEL[key] || f.path) : f.path;
    if (!window.confirm(permanent
      ? `Permanently delete everything in ${label}? This cannot be undone.`
      : `Move everything in ${label} to Deleted Items?`)) return;
    try {
      const { total, failed } = await runFolderAction(f, 'empty');
      toast.success(`${permanent ? 'Deleted' : 'Moved'} ${total} message${total === 1 ? '' : 's'}${failed ? ` (${failed} failed)` : ''}`);
      if (selectedFolder?.id === f.id) refreshCurrentFolder();
      loadFolders();
    } catch (e) { toast.error(e.message || 'Could not empty that folder.'); }
  };

  const addMailbox = async (email) => {
    await api.post('mail/shared-mailboxes', { email });
    toast.success(`Added ${email}`);
    loadMailboxes();
  };
  const removeMailbox = async (mb) => {
    if (!window.confirm(`Remove ${mb.email} from your mailbox switcher?`)) return;
    try {
      await api.del(`mail/shared-mailboxes/${mb.id}`);
      if (activeMailbox === mb.email) setActiveMailbox(null);
      loadMailboxes();
    } catch (e) { toast.error(e.message || 'Could not remove that mailbox.'); }
  };

  // ── Undo Send ────────────────────────────────────────────────────────────
  // Nothing below actually calls the network until the countdown elapses —
  // that IS undo send. See scheduledSend.js's header for why Schedule Send
  // (below) is a genuinely different mechanism and does not go through this.
  const startUndoSend = (mode, seed, fields) => {
    setPending({ key: Date.now(), mode, seed, fields, mailboxEmail: activeMailbox });
    setSecondsLeft(UNDO_SEND_SECONDS);
  };

  useEffect(() => {
    if (!pending) return undefined;
    if (secondsLeft <= 0) {
      const { mode, seed, fields, mailboxEmail } = pending;
      const { to, cc, bcc, subject, bodyHtml, attachments, importance, readReceipt, deliveryReceipt } = fields;
      (async () => {
        try {
          if (mode === 'new') {
            await api.post(withMailbox('mail/send', mailboxEmail), { to, cc, bcc, subject, body: bodyHtml, bodyType: 'html', attachments, importance, readReceipt, deliveryReceipt });
          } else if (mode === 'draft') {
            await api.put(withMailbox(msgPath(seed.draftId), mailboxEmail), { to, cc, bcc, subject, body: bodyHtml, bodyType: 'html', importance });
            await api.post(withMailbox(msgPath(seed.draftId, '/send'), mailboxEmail));
          } else if (mode === 'reply' || mode === 'replyAll') {
            await api.post(withMailbox(msgPath(seed.messageId, '/reply'), mailboxEmail), { body: bodyHtml, bodyType: 'html', replyAll: mode === 'replyAll' });
          } else {
            await api.post(withMailbox(msgPath(seed.messageId, '/forward'), mailboxEmail), { to, comment: bodyHtml, bodyType: 'html' });
          }
          toast.success('Sent');
          if (mailboxEmail === activeMailbox) refreshCurrentFolder();
        } catch (e) { toast.error(e.message || 'Could not send that.'); }
        setPending(null);
      })();
      return undefined;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, secondsLeft]);

  // Undo reopens the compose window with everything intact — clicking Send
  // was a decision to stop editing, not to discard the draft.
  const undoSend = () => {
    if (!pending) return;
    const { mode, seed, fields } = pending;
    setCompose({ mode, seed: { ...seed, to: fields.to, cc: fields.cc, bcc: fields.bcc, subject: fields.subject, body: fields.bodyHtml, attachments: fields.attachments, importance: fields.importance, readReceipt: fields.readReceipt, deliveryReceipt: fields.deliveryReceipt } });
    setPending(null);
  };

  // ── Schedule Send ──────────────────────────────────────────────────────────
  // Calls the API immediately (with sendAt set) — this is a REAL, server-
  // persisted deferral with its own longer-window Undo (mail/scheduled-sends/
  // {id}/cancel), so it does not go through the client-side delay above.
  const scheduleSend = async (mode, seed, fields, sendAtIso) => {
    const { to, cc, bcc, subject, bodyHtml, attachments, importance, readReceipt, deliveryReceipt } = fields;
    let r;
    if (mode === 'new') {
      r = await api.post(withMailbox('mail/send', activeMailbox), { to, cc, bcc, subject, body: bodyHtml, bodyType: 'html', attachments, importance, readReceipt, deliveryReceipt, sendAt: sendAtIso });
    } else if (mode === 'draft') {
      await api.put(withMailbox(msgPath(seed.draftId), activeMailbox), { to, cc, bcc, subject, body: bodyHtml, bodyType: 'html', importance });
      r = await api.post(withMailbox(msgPath(seed.draftId, '/send'), activeMailbox), { sendAt: sendAtIso });
    } else if (mode === 'reply' || mode === 'replyAll') {
      r = await api.post(withMailbox(msgPath(seed.messageId, '/reply'), activeMailbox), { body: bodyHtml, bodyType: 'html', replyAll: mode === 'replyAll', sendAt: sendAtIso });
    } else {
      r = await api.post(withMailbox(msgPath(seed.messageId, '/forward'), activeMailbox), { to, comment: bodyHtml, bodyType: 'html', sendAt: sendAtIso });
    }
    toast.success(`Scheduled for ${fmt.stamp(sendAtIso)}`);
    if (r?.scheduled) loadScheduledSends();
    refreshCurrentFolder();
  };

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  // Read through a ref so the listener is attached once and always sees the
  // current message, list and handlers (mailShortcutFor ignores keys typed
  // into a field, and nothing fires while composing or a dialog is open).
  const shortcutRef = useRef(null);
  shortcutRef.current = (action) => {
    const current = detail || messages.find((x) => x.id === selectedId) || null;
    const idx = messages.findIndex((x) => x.id === selectedId);
    switch (action) {
      case 'new': setCompose({ mode: 'new', seed: {} }); return true;
      case 'help': setShortcutsOpen(true); return true;
      case 'next': { const n = messages[Math.min(messages.length - 1, idx + 1)]; if (n) openMessage(n); return !!n; }
      case 'prev': { const n = messages[Math.max(0, idx - 1)]; if (n && idx > 0) openMessage(n); return !!n; }
      default: break;
    }
    if (!current) return false;
    switch (action) {
      case 'reply': if (detail) openReply(false); return true;
      case 'replyAll': if (detail) openReply(true); return true;
      case 'forward': if (detail) openForward(); return true;
      case 'archive': moveToWellKnown('archive', [current.id]); return true;
      case 'delete': quickDelete(current); return true;
      case 'toggleRead': quickToggleRead(current); return true;
      case 'flag': setFlag(current, current.flagged ? 'clear' : 'noDate'); return true;
      case 'move': setMovingIds([current.id]); return true;
      default: return false;
    }
  };
  const dialogOpen = !!(compose || movingIds || newFolder || renaming || addMailboxOpen || triageOpen
    || categorizing || filingMsg || headers !== undefined || signatureOpen || shortcutsOpen);
  useEffect(() => {
    if (!connected || dialogOpen) return undefined;
    const onKey = (e) => {
      const action = mailShortcutFor(e);
      if (action && shortcutRef.current && shortcutRef.current(action)) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [connected, dialogOpen]);

  const folderNames = useMemo(() => {
    const m = new Map();
    for (const f of folders) m.set(f.id, f.path);
    for (const [key, id] of Object.entries(wellKnown || {})) m.set(id, WELL_KNOWN_LABEL[key] || key);
    return m;
  }, [folders, wellKnown]);
  const searchingAll = searchAll && !!search.trim();
  const isInbox = selectedFolder && (selectedFolder.wellKnownKey === 'inbox' || selectedFolder.id === wellKnown.inbox);

  if (statusLoading) return <Spinner label="Loading Mail…" />;
  if (!connected) return <NotConnectedCard feature="Mail" status={status} setPage={setPage} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}>
      <PageHeader title="Mail" icon="mail" actions={
        <>
          <Btn variant="ghost" onClick={syncMail} loading={syncing} title="Check Outlook for new mail now"><Icon name="refresh" size={14} /> Sync</Btn>
          <Btn variant="ghost" onClick={() => setShortcutsOpen(true)} title="Keyboard shortcuts (?)"><Icon name="info" size={14} /></Btn>
          <Btn variant="ghost" onClick={() => setSignatureOpen(true)}><Icon name="pen" size={14} /> Signature</Btn>
          <Btn variant="ghost" onClick={() => setTriageOpen(true)}><Icon name="warning" size={14} /> Triage inbox</Btn>
          <Btn onClick={() => setCompose({ mode: 'new', seed: {} })}><Icon name="add" size={14} /> New message</Btn>
        </>
      } />

      <div style={{ display: 'flex', flex: 1, gap: space.lg, minHeight: 0 }}>
        <div style={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2, borderRight: `1px solid ${B.border}`, paddingRight: space.md, overflowY: 'auto' }}>
          <MailboxSwitcher mailboxes={mailboxes} active={activeMailbox} onSelect={setActiveMailbox}
            onAdd={() => setAddMailboxOpen(true)} onRemove={removeMailbox} />
          {foldersLoaded ? (
            <>
              <Btn size="sm" variant="ghost" onClick={() => setNewFolder(true)} style={{ justifyContent: 'flex-start', marginBottom: space.sm }}>
                <Icon name="newFolder" size={14} /> New folder
              </Btn>
              {orderedFolders(folders, wellKnown).map((f) => {
                const depth = f.wellKnownKey ? 0 : (f.path.match(/\//g) || []).length;
                const label = f.wellKnownKey ? (WELL_KNOWN_LABEL[f.wellKnownKey] || f.path) : f.path.split('/').pop();
                const active = f.id === selectedFolder?.id;
                return (
                  <div key={f.id} className="harvus-mail-folder-row" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button onClick={() => setSelectedFolder(f)} title={f.path}
                      style={{
                        flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0,
                        padding: '7px 10px', paddingLeft: 10 + depth * 14, border: 'none', borderRadius: radius.lg,
                        background: active ? B.tealLt : 'transparent', color: active ? B.navyDk : B.text,
                        fontSize: font.sm, fontWeight: active ? weight.semibold : weight.regular,
                        cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                      }}
                      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = B.hover; }}
                      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
                      <Icon name={f.wellKnownKey ? (WELL_KNOWN_ICON[f.wellKnownKey] || 'folder') : 'folder'} size={14} style={{ flexShrink: 0, opacity: 0.75 }} />
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                      {!!f.unread && <span style={{ fontSize: 11, fontWeight: weight.bold, color: active ? B.navy : B.textMd }}>{f.unread}</span>}
                    </button>
                    <PopMenu width={200}
                      trigger={({ toggle }) => (
                        <button title="Folder actions" onClick={toggle} style={{ background: 'none', border: 'none', cursor: 'pointer', color: B.textLt, padding: 2 }}>
                          <Icon name="moreVertical" size={12} />
                        </button>
                      )}
                      items={[
                        { label: 'Mark all as read', icon: 'check', onClick: () => markFolderRead(f) },
                        { label: f.wellKnownKey === 'deleteditems' || f.wellKnownKey === 'junkemail' ? 'Empty folder' : 'Empty to Deleted Items', icon: 'trash', danger: true, onClick: () => emptyFolder(f) },
                        !f.wellKnownKey && { divider: true },
                        !f.wellKnownKey && { label: 'Rename', icon: 'edit', onClick: () => setRenaming(f) },
                        !f.wellKnownKey && { label: 'Delete folder', icon: 'trash', danger: true, onClick: () => deleteFolder(f) },
                      ]} />
                  </div>
                );
              })}
              <button onClick={() => setSelectedFolder({ id: SCHEDULED_SENTINEL_ID, path: 'Scheduled' })}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', marginTop: 6,
                  border: 'none', borderRadius: radius.lg, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                  background: onScheduledView ? B.tealLt : 'transparent', color: onScheduledView ? B.navyDk : B.text,
                  fontSize: font.sm, fontWeight: onScheduledView ? weight.semibold : weight.regular,
                }}>
                <Icon name="clock" size={14} style={{ flexShrink: 0, opacity: 0.75 }} />
                <span style={{ flex: 1 }}>Scheduled</span>
                {scheduledSends.length > 0 && <span style={{ fontSize: 11, fontWeight: weight.bold, color: B.textMd }}>{scheduledSends.length}</span>}
              </button>
            </>
          ) : <Spinner label="" />}
        </div>

        <div style={{ width: 340, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${B.border}`, minHeight: 0 }}>
          {onScheduledView ? (
            <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.text, marginBottom: space.sm, flexShrink: 0 }}>Scheduled</div>
          ) : selectedIds.size > 0 ? (
            <div style={{
              display: 'flex', gap: 4, alignItems: 'center', marginBottom: space.sm, flexShrink: 0,
              padding: '6px 8px', background: B.navy, borderRadius: radius.lg, color: '#fff', fontSize: font.sm,
            }}>
              <span style={{ fontWeight: weight.semibold, whiteSpace: 'nowrap' }}>{selectedIds.size} selected</span>
              <span style={{ flex: 1 }} />
              <ToolbarBtn icon="mailOpen" title="Mark read" disabled={bulkBusy} onClick={() => bulkMarkRead(true)} />
              <ToolbarBtn icon="mail" title="Mark unread" disabled={bulkBusy} onClick={() => bulkMarkRead(false)} />
              <ToolbarBtn icon="archive" title="Archive" disabled={bulkBusy} onClick={() => moveToWellKnown('archive', Array.from(selectedIds))} />
              <ToolbarBtn icon="warning" title={inJunk ? 'Not junk' : 'Junk'} disabled={bulkBusy} onClick={() => moveToWellKnown(inJunk ? 'inbox' : 'junkemail', Array.from(selectedIds))} />
              <ToolbarBtn icon="folder" title="Move to folder" disabled={bulkBusy} onClick={() => setMovingIds(Array.from(selectedIds))} />
              <ToolbarBtn icon="trash" title="Delete" disabled={bulkBusy} onClick={bulkDelete} />
              <ToolbarBtn icon="close" title="Clear selection" onClick={clearSelection} />
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 6, marginBottom: space.sm, flexShrink: 0, alignItems: 'center' }}>
              <Tick checked={allSelected} indeterminate={someSelected} onChange={toggleSelectAll} label="Select all" />
              <div style={{ flex: 1 }}>
                <Input value={search} onChange={setSearch} placeholder={searchAll ? 'Search all folders…' : 'Search this folder…'} />
              </div>
              <Btn size="sm" variant={unreadOnly ? 'secondary' : 'ghost'} onClick={() => setUnreadOnly((v) => !v)} title="Unread only">
                <Icon name="mail" size={13} />
              </Btn>
              <Btn size="sm" variant={flaggedOnly ? 'secondary' : 'ghost'} onClick={() => setFlaggedOnly((v) => !v)} title="Flagged only">
                <Icon name="flag" size={13} color={flaggedOnly ? undefined : B.textLt} />
              </Btn>
            </div>
          )}
          {!onScheduledView && selectedIds.size === 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: space.sm, flexShrink: 0, fontSize: font.xs, color: B.textMd, flexWrap: 'wrap' }}>
              {isInbox && !search.trim() && (
                <div style={{ display: 'flex', border: `1px solid ${B.border}`, borderRadius: radius.pill, overflow: 'hidden' }}>
                  {[['all', 'All'], ['focused', 'Focused'], ['other', 'Other']].map(([k, label]) => (
                    <button key={k} onClick={() => setFocus(k)}
                      style={{
                        border: 'none', padding: '3px 10px', cursor: 'pointer', fontFamily: 'inherit', fontSize: font.xs,
                        background: focus === k ? B.navy : 'transparent', color: focus === k ? '#fff' : B.textMd,
                      }}>{label}</button>
                  ))}
                </div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                Sort
                <select value={sort} onChange={(e) => { const v = e.target.value; setSort(v); setSortDir(v === 'date' || v === 'importance' ? 'desc' : 'asc'); }}
                  disabled={!!search.trim()} title={search.trim() ? 'Search results are ordered by relevance' : 'Sort by'}
                  style={{ fontFamily: 'inherit', fontSize: font.xs, padding: '2px 4px', borderRadius: radius.md, border: `1px solid ${B.border}` }}>
                  <option value="date">Date</option><option value="from">From</option>
                  <option value="subject">Subject</option><option value="importance">Importance</option>
                </select>
                <button onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))} disabled={!!search.trim()}
                  title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: B.textMd, padding: 0, display: 'flex' }}>
                  <Icon name={sortDir === 'asc' ? 'arrowUp' : 'arrowDown'} size={12} />
                </button>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }} title="Search every folder, not just this one">
                <input type="checkbox" checked={searchAll} onChange={(e) => setSearchAll(e.target.checked)} /> All folders
              </label>
            </div>
          )}
          {onScheduledView ? (
            <ScheduledList scheduledSends={scheduledSends} mailboxEmail={activeMailbox} onChanged={loadScheduledSends} />
          ) : (
            <div style={{ flex: 1, overflowY: 'auto', border: `1px solid ${B.border}`, borderRadius: radius.lg }}>
              {loadingMessages && messages.length === 0 && <Spinner label="Loading…" />}
              {!loadingMessages && messages.length === 0 && (
                <div style={{ padding: space.xl, textAlign: 'center', color: B.textMd, fontSize: font.sm }}>Nothing here.</div>
              )}
              {threadGroups.map((group) => (
                <ThreadRow key={group.key} group={group} activeId={selectedId}
                  hoveredId={hoveredId} setHoveredId={setHoveredId}
                  selectedIds={selectedIds} expanded={expandedThreads.has(group.key)}
                  onToggleExpand={(key) => setExpandedThreads((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key); else next.add(key);
                    return next;
                  })}
                  onOpen={openMessage} onToggleSelect={toggleSelect} showCheckbox={selectedIds.size > 0}
                  onQuickToggleRead={quickToggleRead} onQuickDelete={quickDelete}
                  folderLabelOf={searchingAll ? (m) => folderNames.get(m.parentFolderId) || null : null} />
              ))}
              {nextPageToken && (
                <div style={{ padding: space.md, textAlign: 'center' }}>
                  <Btn size="sm" variant="ghost" loading={loadingMessages}
                    onClick={() => loadMessages(selectedFolder.id, { append: true, pageToken: nextPageToken })}>
                    Load more
                  </Btn>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
          {compose ? (
            <ComposeModal key={`${compose.mode}-${compose.seed.draftId || compose.seed.messageId || 'new'}`}
              mode={compose.mode} seed={compose.seed} mailboxEmail={activeMailbox} signature={signature} onClose={() => setCompose(null)}
              onSendNow={startUndoSend} onScheduled={scheduleSend} />
          ) : onScheduledView ? (
            <EmptyState icon="clock" title="Scheduled messages" message="Pick one on the left to undo or watch it go out." />
          ) : (
            <>
              {loadingDetail && <Spinner label="Loading message…" />}
              {!loadingDetail && !detail && (
                <EmptyState icon="mail" title="Select a message" message="Pick a message from the list to read it here." />
              )}
              {!loadingDetail && detail && (
                <Card>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.md, marginBottom: space.md }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: font.lg, fontWeight: weight.bold, color: B.text }}>{detail.subject}</div>
                      <div style={{ fontSize: font.sm, color: B.textMd, marginTop: 4 }}>
                        From <strong>{detail.from ? (detail.from.name || detail.from.address) : 'Unknown'}</strong>
                        {detail.receivedAt ? ` — ${fmt.stamp(detail.receivedAt)}` : ''}
                      </div>
                      <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 2 }}>To: {addressLine(detail.to) || '—'}</div>
                      {detail.cc?.length > 0 && <div style={{ fontSize: font.xs, color: B.textLt }}>Cc: {addressLine(detail.cc)}</div>}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                        {detail.importance === 'high' && <Chip color={B.red}>High importance</Chip>}
                        {detail.flagged && <Chip color={B.red}><Icon name="flag" size={11} /> {flagDueLabel(detail.flagDue)?.text || 'Flagged'}</Chip>}
                        {detail.flagStatus === 'complete' && <Chip color={B.green}><Icon name="check" size={11} /> Completed</Chip>}
                        {(detail.categories || []).map((c) => <Chip key={c} onClick={() => setCategorizing(detail)}>{c}</Chip>)}
                        {detail.readReceiptRequested && <Chip color={B.amber}>Sender asked for a read receipt</Chip>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <Btn size="sm" variant="ghost" onClick={() => openReply(false)}><Icon name="reply" size={13} /> Reply</Btn>
                      <Btn size="sm" variant="ghost" onClick={() => openReply(true)}><Icon name="replyAll" size={13} /> Reply all</Btn>
                      <Btn size="sm" variant="ghost" onClick={openForward}><Icon name="forward" size={13} /> Forward</Btn>
                      <Btn size="sm" variant="ghost" onClick={() => setFilingMsg(detail)} title="File this email to a deal"><Icon name="deals" size={13} /> File to deal</Btn>
                      <PopMenu width={190}
                        trigger={({ toggle }) => (
                          <Btn size="sm" variant="ghost" onClick={toggle} title="Flag for follow-up">
                            <Icon name="flag" size={13} color={detail.flagged ? B.red : undefined} /><Icon name="chevronDown" size={10} />
                          </Btn>
                        )}
                        items={flagMenuItems(detail)} />
                      <Btn size="sm" variant="ghost" onClick={() => moveToWellKnown('archive', [detail.id])} title="Archive (e)"><Icon name="archive" size={13} /></Btn>
                      <Btn size="sm" variant="ghost" onClick={doDelete} title="Delete (#)"><Icon name="trash" size={13} /></Btn>
                      <PopMenu width={230}
                        trigger={({ toggle }) => (
                          <Btn size="sm" variant="ghost" onClick={toggle} title="More actions"><Icon name="more" size={13} /></Btn>
                        )}
                        items={[
                          { label: detail.isRead ? 'Mark as unread' : 'Mark as read', icon: 'mail', hint: 'u', onClick: toggleUnread },
                          { label: 'Move to folder…', icon: 'folder', hint: 'v', onClick: () => setMovingIds([detail.id]) },
                          { label: 'Categorize…', icon: 'pushpin', onClick: () => setCategorizing(detail) },
                          { label: inJunk ? 'Not junk — move to Inbox' : 'Mark as junk', icon: 'warning', onClick: () => moveToWellKnown(inJunk ? 'inbox' : 'junkemail', [detail.id]) },
                          { divider: true },
                          { label: 'Print', icon: 'print', onClick: () => printMessage(detail) },
                          { label: 'Download (.eml)', icon: 'download', onClick: () => downloadEml(detail) },
                          { label: 'View message details', icon: 'info', onClick: () => viewHeaders(detail) },
                          detail.webLink && { label: 'Open in Outlook on the web', icon: 'external', onClick: () => window.open(detail.webLink, '_blank', 'noopener') },
                        ]} />
                    </div>
                  </div>

                  {detail.attachments?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: space.md }}>
                      {detail.attachments.map((a) => (
                        <a key={a.id} href={`/api/${msgPath(detail.id, `/attachments/${encodeURIComponent(a.id)}`)}`} target="_blank" rel="noreferrer"
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              const r = await api.get(withMailbox(msgPath(detail.id, `/attachments/${encodeURIComponent(a.id)}`), activeMailbox));
                              const link = document.createElement('a');
                              link.href = `data:${r.contentType};base64,${r.contentBytes}`;
                              link.download = r.name;
                              link.click();
                            } catch (err) { toast.error(err.message || 'Could not download that attachment.'); }
                          }}
                          style={{ textDecoration: 'none' }}>
                          <Chip><Icon name="attach" size={12} /> {a.name}</Chip>
                        </a>
                      ))}
                    </div>
                  )}

                  {detail.bodyType === 'html' ? <SafeHtmlFrame html={detail.body} /> : (
                    <div style={{ whiteSpace: 'pre-wrap', fontSize: font.base, color: B.text, lineHeight: 1.6 }}>{detail.body}</div>
                  )}
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {movingIds && (
        <MoveModal folders={folders} wellKnown={wellKnown} currentFolderId={selectedFolder?.id}
          title={movingIds.length > 1 ? `Move ${movingIds.length} messages` : 'Move to folder'}
          onClose={() => setMovingIds(null)} onMove={(target) => doMove(target, movingIds)} />
      )}
      {newFolder && <FolderNameModal title="New folder" onClose={() => setNewFolder(false)} onSave={createFolder} />}
      {renaming && <FolderNameModal title="Rename folder" initial={renaming.path.split('/').pop()} onClose={() => setRenaming(null)} onSave={renameFolder} />}
      {addMailboxOpen && <AddMailboxModal onClose={() => setAddMailboxOpen(false)} onAdd={addMailbox} />}
      {triageOpen && (
        <MailTriageModal
          onClose={() => setTriageOpen(false)}
          onOpenMessage={(m) => { setTriageOpen(false); openMessage(m); }}
        />
      )}
      {categorizing && (
        <CategoriesModal initial={categorizing.categories} onClose={() => setCategorizing(null)}
          onSave={(cats) => saveCategories(categorizing, cats)} />
      )}
      {headers !== undefined && <HeadersModal data={headers} onClose={() => setHeaders(undefined)} />}
      {filingMsg && (
        <FileToDealModal
          messageId={filingMsg.id}
          subject={filingMsg.subject}
          mailbox={activeMailbox}
          onClose={() => setFilingMsg(null)}
          onFiled={(r) => {
            if (!r?.categorized) return;
            const tag = 'Filed to Harvus';
            const add = (m) => (m && m.id === filingMsg.id && !(m.categories || []).includes(tag) ? { ...m, categories: [...(m.categories || []), tag] } : m);
            setDetail((d) => add(d));
            setMessages((list) => list.map(add));
          }}
        />
      )}
      {signatureOpen && <SignatureModal initial={signature} onClose={() => setSignatureOpen(false)} onSave={saveSignature} />}
      {shortcutsOpen && <ShortcutsModal onClose={() => setShortcutsOpen(false)} />}
      {pending && <UndoSendBanner secondsLeft={secondsLeft} onUndo={undoSend} />}
    </div>
  );
};
